/* serviceLines.js
 *
 * THE ONE PLACE rows and `servicesIncluded` HTML convert into each other.
 *
 * `servicesIncluded` stays the field every downstream surface reads — the
 * client portal, the contract generator and the printed contract all take it
 * as HTML and none of them know rows exist. The row editor is an INPUT
 * method: it edits rows, then renders them back to that same HTML on save.
 * That is why adding rows cannot break any of those surfaces.
 *
 * Any new surface that needs the rows imports from here. It must not carry
 * its own copy of this parsing.
 */

import { formatMoney } from './formatMoney';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const blankLine = () => ({ name: '', description: '', amount: null });

/* Rows the editor can work with, from whatever the proposal has today.
   Priority: stored rows first, then parse the saved HTML, then nothing. */
export function linesFrom(content) {
  const c = content || {};
  const stored = (Array.isArray(c.serviceItems) ? c.serviceItems : [])
    .filter((it) => it && typeof it === 'object' && String(it.name || '').trim());
  // `servicesIncluded` is what every downstream surface renders, so it is the
  // truth here too. Stored rows are only consulted to carry an `amount` across
  // for a line with the same name — otherwise a save that wrote just the HTML
  // would leave stale rows behind that win on the next open.
  const parsed = linesFromHtml(c.servicesIncluded);
  if (parsed.length) {
    const amountByName = new Map();
    for (const it of stored) {
      const a = Number(it.amount);
      if (Number.isFinite(a)) amountByName.set(String(it.name).trim().toLowerCase(), a);
    }
    return parsed.map((row) => {
      const a = amountByName.get(String(row.name).trim().toLowerCase());
      return Number.isFinite(a) ? { ...row, amount: a } : row;
    });
  }
  // Nothing saved as HTML yet — fall back to the generated rows.
  return stored.map((it) => ({
    name: String(it.name || '').trim(),
    description: String(it.description || '').trim(),
    amount: Number.isFinite(Number(it.amount)) ? Number(it.amount) : null,
  }));
}

/* Parse an existing <ul><li>… list, or a bulleted plain-text block, into rows.
   Splits a line on an em dash or " - " into name + description. A line with
   no separator becomes a name with an empty description — never the reverse,
   because the name is the part that always exists. */
export function linesFromHtml(html) {
  const raw = String(html || '');
  if (!raw.trim()) return [];
  let parts = [];
  const liMatches = raw.match(/<li\b[^>]*>([\s\S]*?)<\/li\s*>/gi);
  if (liMatches && liMatches.length) {
    parts = liMatches.map((m) => m.replace(/<li\b[^>]*>/i, '').replace(/<\/li\s*>/i, ''));
  } else {
    parts = raw.split(/<br\s*\/?>|\n/i);
  }
  const out = [];
  for (const p of parts) {
    const text = String(p)
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .replace(/^[\u2022\-\*]\s+/, '')
      .trim();
    if (!text) continue;
    const m = text.match(/^(.*?)\s+(?:\u2014|\u2013|-)\s+(.*)$/);
    out.push(m
      ? { name: m[1].trim(), description: m[2].trim(), amount: null }
      : { name: text, description: '', amount: null });
  }
  return out;
}

/* Drop rows with no name — an unnamed row is not a service. */
export function usableLines(lines) {
  return (Array.isArray(lines) ? lines : [])
    .filter((l) => l && String(l.name || '').trim());
}

/* Rows back to the HTML that every downstream surface reads. */
export function linesToHtml(lines) {
  const items = usableLines(lines);
  if (!items.length) return '';
  const li = items.map((l) => {
    const name = esc(String(l.name).trim());
    const desc = String(l.description || '').trim();
    return desc ? `<li>${name} \u2014 ${esc(desc)}</li>` : `<li>${name}</li>`;
  }).join('');
  return `<ul>${li}</ul>`;
}

/* Rows to the client "What's inside your package" list — the TITLE in bold,
   followed by its description. These are the SAME rows the chips render from,
   so the two surfaces always contain the same items in the same order. */
export function linesToTitledHtml(lines) {
  const items = usableLines(lines);
  if (!items.length) return '';
  const li = items.map((l) => {
    const name = esc(String(l.name).trim());
    const desc = String(l.description || '').trim();
    return desc
      ? `<li><strong>${name}</strong> \u2014 ${esc(desc)}</li>`
      : `<li><strong>${name}</strong></li>`;
  }).join('');
  return `<ul>${li}</ul>`;
}

/* What the PATCH body should contain for a row save. Both keys, always,
   so the rows and the HTML can never drift apart. */
export function lineSavePayload(lines) {
  const items = usableLines(lines).map((l) => {
    const row = { name: String(l.name).trim(), description: String(l.description || '').trim() };
    if (Number.isFinite(Number(l.amount)) && l.amount !== null && l.amount !== '') {
      row.amount = Number(l.amount);
    }
    return row;
  });
  return { serviceItems: items, servicesIncluded: linesToHtml(items) };
}

/* Same payload, plus the per-event price switch. Saved in the SAME request
   as the rows so the switch and the amounts can never land separately. */
export function lineSavePayloadWithPrices(lines, showPrices) {
  return { ...lineSavePayload(lines), showLinePrices: !!showPrices };
}

/* ── Prices ────────────────────────────────────────────────────────────
   `pricedLines` is the ONLY place that decides whether prices may be shown.
   The editor warning, the client portal and the printed contract all ask it.
   A partially-priced package, or one whose lines do not add up to the
   contract total, returns null and every surface falls back to the plain
   list. There is deliberately no "close enough" and no second opinion. */

/* A blank price is NOT zero. `Number(null)` and `Number('')` are both 0, and
   0 is finite — so a naive isFinite check treats an empty box as a real $0
   and lets a half-priced package reconcile. This mirrors the backend's
   `amount is not None` exactly. Every price check goes through here. */
export function hasAmount(line) {
  const a = line ? line.amount : null;
  if (a === null || a === undefined || a === '') return false;
  return Number.isFinite(Number(a));
}

export function sumLines(lines) {
  return usableLines(lines).reduce((n, l) => (hasAmount(l) ? n + Number(l.amount) : n), 0);
}

export function linesReconcile(lines, total) {
  const items = usableLines(lines);
  const t = Number(total);
  if (!items.length || !Number.isFinite(t) || t <= 0) return false;
  // Every line must carry a real amount. One blank price means the package
  // is half-priced, and half-priced is not a document we send anyone.
  if (!items.every(hasAmount)) return false;
  return Math.abs(sumLines(items) - t) < 0.005;
}

export function pricedLines(lines, total, showPrices) {
  if (!showPrices) return null;
  if (!linesReconcile(lines, total)) return null;
  return usableLines(lines).map((l) => ({
    name: String(l.name).trim(),
    description: String(l.description || '').trim(),
    amount: Number(l.amount),
  }));
}

/* The priced table, as HTML, for surfaces that render `servicesIncluded`
   as markup: the closed section in the workspace and the client portal.
   Goes through `pricedLines`, so it returns '' for a half-priced or
   mismatched package and the caller falls back to the plain list. The
   printed contract builds the same table in JSX from the backend's copy of
   this decision — same rule, two renderers, one guard. */
export function linesToPricedHtml(lines, total, showPrices) {
  const priced = pricedLines(lines, total, showPrices);
  if (!priced) return '';
  const money = (n) => formatMoney(n);
  const cell = 'padding:6px 0;border-bottom:1px solid rgba(255,255,255,0.06);';
  const foot = 'padding:9px 0 0 0;border-top:1px solid rgba(212,175,55,0.45);font-weight:700;';
  const body = priced.map((l) => (
    '<tr><td style="' + cell + 'vertical-align:top;"><strong>' + esc(l.name) + '</strong>' +
      (l.description
        ? '<span style="display:block;opacity:0.62;font-size:11.5px;margin-top:1px;">' + esc(l.description) + '</span>'
        : '') +
    '</td><td style="' + cell + 'text-align:right;white-space:nowrap;font-weight:600;">' +
      money(l.amount) +
    '</td></tr>'
  )).join('');
  const sum = priced.reduce((n, l) => n + l.amount, 0);
  return '<table style="width:100%;border-collapse:collapse;">' + body +
    '<tr><td style="' + foot + '">Total</td>' +
    '<td style="' + foot + 'text-align:right;white-space:nowrap;">' + money(sum) + '</td></tr>' +
    '</table>';
}

/* Admin-only breakdown for the workspace. Unlike `linesToPricedHtml` this has
   NO show-prices gate and NO reconcile gate — the admin always sees the
   line-by-line numbers in their own workspace. Reuses the exact same table,
   cell and footer styles so it looks identical. A line with no amount shows an
   em dash; the footer is labeled "Running total" and sums the amounts present.
   Returns '' only when there are no usable lines. Not used by the client portal
   or the printed/sent contract. */
export function linesToAdminHtml(lines) {
  const items = usableLines(lines);
  if (!items.length) return '';
  const money = (n) => formatMoney(n);
  const cell = 'padding:6px 0;border-bottom:1px solid rgba(255,255,255,0.06);';
  const foot = 'padding:9px 0 0 0;border-top:1px solid rgba(212,175,55,0.45);font-weight:700;';
  const body = items.map((l) => (
    '<tr><td style="' + cell + 'vertical-align:top;"><strong>' + esc(String(l.name).trim()) + '</strong>' +
      (String(l.description || '').trim()
        ? '<span style="display:block;opacity:0.62;font-size:11.5px;margin-top:1px;">' + esc(String(l.description).trim()) + '</span>'
        : '') +
    '</td><td style="' + cell + 'text-align:right;white-space:nowrap;font-weight:600;">' +
      (hasAmount(l) ? money(Number(l.amount)) : '&mdash;') +
    '</td></tr>'
  )).join('');
  const sum = sumLines(items);
  return '<table style="width:100%;border-collapse:collapse;">' + body +
    '<tr><td style="' + foot + '">Running total</td>' +
    '<td style="' + foot + 'text-align:right;white-space:nowrap;">' + money(sum) + '</td></tr>' +
    '</table>';
}
